import { useEffect, useRef, useState } from 'react';
import { stripInline } from '@wds/astro-bot-shared/course/inline';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { Figure } from '../../components/course/CoursePictures';
import { InlineText } from '../../components/course/InlineText';
import { Exercise } from '../../components/course/Exercise';
import { FeedbackForm } from '../../components/course/FeedbackForm';
import { Quiz } from '../../components/course/Quiz';
import { Theory } from '../../components/course/Theory';
import {
  CourseApiError,
  incompleteDetails,
  isTooOften,
  useAnswerQuiz,
  useCompleteLesson,
  useCourseState,
  useLesson,
  useMarkAnswer,
  useSaveAnswer,
} from '../../hooks/useCourse';
import { useLessonTracking } from '../../hooks/useLessonTracking';
import { lessonMissing, type MissingItem } from '../../lib/course-completion';
import { courseChatHref } from '../../lib/chat-links';
import { isFirstHistoryEntry } from '../../lib/history-back';
import { itemLabel, itemPath, nextOpenItem } from '../../lib/course-items';
import type { LessonDto } from '../../types/course';
import styles from './LessonPage.module.css';

const attr = (v: string): string => v.replace(/["\\]/g, '\\$&');

/** Scrolls to the card of a missing item and focuses the control the learner needs there. */
function goToMissing(root: HTMLElement | null, m: MissingItem): void {
  const id = attr(m.id);
  const card = root?.querySelector<HTMLElement>(`[data-testid="${m.kind === 'quiz' ? 'quiz' : 'exercise'}-${id}"]`);
  if (!card) return;
  const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  card.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  const order =
    m.kind === 'answer' ? [`answer-${id}`]
      : m.kind === 'mark' ? [`mark-ok-${id}`, `show-solution-${id}`, `answer-${id}`]
        : [`quiz-${id}-retry`, `quiz-${id}-opt-0`];
  for (const testId of order) {
    const el = card.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
    if (el && !(el as HTMLButtonElement).disabled) { el.focus({ preventScroll: true }); return; }
  }
}

/** Keyed by the lesson code: «Дальше» to the next lesson starts with fresh drafts, errors and marks. */
export function LessonPage(): JSX.Element {
  const { code = '' } = useParams();
  return <LessonView key={code} code={code} />;
}

function LessonView({ code }: { code: string }): JSX.Element {
  const { t } = useTranslation('course');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  // Lessons open only from the course page, so «to course» is a step back (no second /course entry in history).
  const backToCourse = (): void => { if (isFirstHistoryEntry()) navigate('/course', { replace: true }); else navigate(-1); };
  const lessonQ = useLesson(code);
  const courseQ = useCourseState();
  const save = useSaveAnswer(code);
  const mark = useMarkAnswer(code);
  const answer = useAnswerQuiz(code);
  const complete = useCompleteLesson(code);
  const [itemError, setItemError] = useState<{ key: string; error: unknown; starsPending?: boolean } | null>(null);
  const [completeError, setCompleteError] = useState<unknown>(null);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const bodyRef = useRef<HTMLDivElement>(null);
  const [incomplete, setIncomplete] = useState<{ exercises: string[]; quiz: string[] } | null>(null);

  const lesson: LessonDto | undefined = lessonQ.data;

  // A visit and the scroll milestones of the lesson body; never blocks or disturbs the page.
  useLessonTracking({ kind: 'lesson', code, versionNumber: lesson?.versionNumber, contentRef: bodyRef, enabled: !!lesson });

  // `#feedback` (the «Оставить отзыв» link from the bot): bring the form into view once it is on the page.
  const hash = useLocation().hash;
  const feedbackRef = useRef<HTMLElement>(null);
  const feedbackShown = useRef(false);
  const showFeedback = !!lesson?.completed;
  useEffect(() => {
    if (hash !== '#feedback' || !showFeedback || feedbackShown.current) return;
    const el = feedbackRef.current;
    if (!el) return;
    feedbackShown.current = true;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [hash, showFeedback]);

  function setDirtyId(id: string, isDirty: boolean): void {
    setDirty((prev) => {
      if (prev.has(id) === isDirty) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(id); else next.delete(id);
      return next;
    });
  }

  /** Runs a mutation; remembers the error under `key`; a vanished item (400/404) means a new lesson version. */
  function wrap<A extends unknown[]>(key: string, fn: (...a: A) => Promise<unknown>) {
    return async (...a: A): Promise<void> => {
      setItemError(null);
      try {
        await fn(...a);
      } catch (e) {
        setItemError({ key, error: e });
        if (e instanceof CourseApiError && (e.status === 400 || e.status === 404)) void lessonQ.refetch();
        throw e;
      }
    };
  }
  const errFor = (key: string): string | null => (itemError?.key === key ? errorText(itemError.error) : null);

  function errorText(e: unknown): string {
    return isTooOften(e) ? t('lesson.tooOften') : t('lesson.error');
  }

  async function handleComplete(): Promise<void> {
    setCompleteError(null);
    setItemError(null);
    setIncomplete(null);
    try {
      await complete.mutateAsync();
      backToCourse();
    } catch (e) {
      const details = incompleteDetails(e);
      if (details) {
        setIncomplete({ exercises: details.missingExercises, quiz: details.missingQuiz });
        // Almost always the owner changed the lesson: load the new version.
        void lessonQ.refetch();
      } else {
        setCompleteError(e);
      }
    }
  }

  const errCode = lessonQ.error instanceof CourseApiError ? lessonQ.error.code : null;
  const toCourse = <Link to="/course" onClick={(e) => { e.preventDefault(); backToCourse(); }} className={styles.link} data-testid="lesson-to-course">{t('lesson.toCourse')} ›</Link>;
  // Banner over a lesson that is already on screen: say why the refresh failed when the server told us.
  const refreshKey = errCode === 'locked' ? 'lesson.refreshLocked' : errCode === 'not_launched' ? 'refreshNotLaunched' : 'lesson.refreshFailed';
  const KNOWN: Record<string, { key: string; testId: string }> = {
    locked: { key: 'lesson.locked', testId: 'lesson-locked' },
    not_enrolled: { key: 'lesson.notEnrolled', testId: 'lesson-not-enrolled' },
    not_launched: { key: 'notLaunched', testId: 'lesson-not-launched' },
  };

  let body: JSX.Element;
  if (!lesson) {
    // Nothing to show yet. Once a lesson is on screen, a failed refetch never replaces it (see the banner below).
    const known = errCode ? KNOWN[errCode] : undefined;
    if (lessonQ.isLoading || (lessonQ.isPending && !lessonQ.isError)) {
      body = <p data-testid="lesson-loading">{tc('states.loading')}</p>;
    } else if (known) {
      body = (
        <>
          <p role="alert" data-testid={known.testId}>{t(known.key)}</p>
          {toCourse}
        </>
      );
    } else {
      body = <p role="alert" data-testid="lesson-error">{tc('errors.generic')}</p>;
    }
  } else {
    const c = lesson.content;
    // The server's rule (rules.ts lessonCompletion), by the saved state: a dirty field counts as what is saved.
    const missing = lessonMissing(c, lesson.answers, lesson.quizCorrect);
    const canComplete = missing.length === 0;
    const missingText = (m: MissingItem): string =>
      m.kind === 'quiz'
        ? t('lesson.remainingQuiz', { n: m.number })
        : t(m.kind === 'answer' ? 'lesson.remainingAnswer' : 'lesson.remainingMark', { title: stripInline(m.title) });
    const exTitle = (id: string): string | undefined => {
      const title = c.exercises.find((e) => e.id === id)?.title;
      return title === undefined ? undefined : stripInline(title); // inside a sentence: no bold
    };
    const qNumber = (id: string): number => c.quiz.findIndex((q) => q.id === id) + 1;
    const hasDirty = dirty.size > 0;
    const next = lesson.completed ? nextOpenItem(courseQ.data, lesson.code) : null;
    body = (
      <>
        {lessonQ.isError && (
          <p role="alert" className={styles.banner} data-testid="lesson-refresh-failed">{t(refreshKey)}</p>
        )}
        {lesson.draft && <p className={styles.banner} data-testid="lesson-draft">{t('lesson.draftBanner')}</p>}
        <h2 className={styles.title}><InlineText text={c.title} /></h2>
        {c.subtitle && <p className={styles.subtitle}><InlineText text={c.subtitle} /></p>}
        <Theory pages={c.pages} />

        {(c.exercises.length > 0 || c.exercisesIntro.length > 0) && (
          <>
            <h3 className={styles.section}>{t('lesson.practice')}</h3>
            {c.exercisesIntro.map((f, i) => (
              <Figure key={i} image={f.image} caption={f.caption} />
            ))}
            {c.exercises.map((ex) => (
              <Exercise
                key={ex.id}
                exercise={ex}
                saved={lesson.answers[ex.id]}
                error={errFor(ex.id)}
                onDiscuss={() => navigate(courseChatHref({ kind: 'exercise', lessonId: lesson.lessonId, targetId: ex.id }))}
                onDirtyChange={setDirtyId}
                onSave={wrap(ex.id, (text: string) => save.mutateAsync({ exerciseId: ex.id, text }))}
                onMark={wrap(ex.id, (m) => mark.mutateAsync({ exerciseId: ex.id, mark: m }))}
              />
            ))}
          </>
        )}

        {c.quiz.length > 0 && (
          <>
            <h3 className={styles.section}>{t('lesson.quizTitle')}</h3>
            {c.quiz.map((q, i) => (
              <Quiz
                key={q.id}
                onDiscuss={() => navigate(courseChatHref({ kind: 'quiz', lessonId: lesson.lessonId, targetId: q.id }))}
                item={q}
                number={i + 1}
                answeredRight={lesson.quizCorrect.includes(q.id)}
                error={errFor(q.id)}
                onAnswer={wrap(q.id, (optionIndex: number) => answer.mutateAsync({ quizId: q.id, optionIndex }))}
              />
            ))}
          </>
        )}

        {completeError !== null && <p role="alert" className={styles.error} data-testid="lesson-action-error">{errorText(completeError)}</p>}
        {incomplete && (
          <div role="alert" data-testid="lesson-incomplete">
            <p className={styles.error}>{t('lesson.incomplete')}</p>
            <ul className={styles.missing}>
              {incomplete.exercises.map((id) => (
                <li key={`e-${id}`}>{exTitle(id) ? t('lesson.missingExercise', { title: exTitle(id) }) : t('lesson.missingNewExercise')}</li>
              ))}
              {incomplete.quiz.map((id) => (
                <li key={`q-${id}`}>{qNumber(id) > 0 ? t('lesson.missingQuiz', { n: qNumber(id) }) : t('lesson.missingNewQuiz')}</li>
              ))}
            </ul>
          </div>
        )}
        {lesson.completed ? (
          <>
            <p className={styles.passed} data-testid="lesson-passed">{t('lesson.passed')}</p>
            <section id="feedback" ref={feedbackRef} className={styles.feedback} data-testid="lesson-feedback">
              <FeedbackForm lessonCode={lesson.code} />
            </section>
            {next ? (
              <button type="button" className={styles.primary} onClick={() => navigate(itemPath(next.kind, next.code), { replace: true })} data-testid="lesson-next">
                {t('lesson.next', { title: itemLabel(t, next) })}
              </button>
            ) : (
              <button type="button" className={styles.primary} onClick={backToCourse} data-testid="lesson-to-course-btn">
                {t('lesson.toCourse')}
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className={styles.primary}
              disabled={!canComplete || hasDirty || complete.isPending}
              onClick={() => void handleComplete()}
              data-testid="lesson-complete"
            >
              {complete.isPending ? t('lesson.completing') : t('lesson.complete')}
            </button>
            {hasDirty && <p className={styles.subtitle} data-testid="lesson-dirty-hint">{t('lesson.saveFirst')}</p>}
            {missing.length > 0 && (
              <div className={styles.remaining} data-testid="lesson-remaining">
                <p className={styles.remainingTitle} id="lesson-remaining-title">{t('lesson.remainingTitle')}</p>
                <ul className={styles.remainingList} aria-labelledby="lesson-remaining-title">
                  {missing.map((m) => (
                    <li key={`${m.kind === 'quiz' ? 'q' : 'e'}-${m.id}`}>
                      <button
                        type="button"
                        className={styles.remainingBtn}
                        onClick={() => goToMissing(bodyRef.current, m)}
                        data-testid={`remaining-${m.kind}-${m.id}`}
                      >
                        {missingText(m)}
                        <span aria-hidden="true"> ›</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <MeSubpageShell title={t('pageTitle')} onBack={backToCourse} data-testid="lesson-page">
      <div className={styles.body} ref={bodyRef}>{body}</div>
    </MeSubpageShell>
  );
}
